import React from "react";
import { Link } from "react-router-dom";
import { FaDiscord, FaGithub } from "react-icons/fa";

const C = {
  bg: "#08090d",
  surface: "#12141b",
  panel: "#0b1020",
  border: "#272b36",
  text: "#f6f7fb",
  muted: "#a7adbb",
  quiet: "#707789",
  blue: "#3b82f6",
  blue2: "#60a5fa",
  green: "#4ade80",
};

const impactStats = [
  "damage",
  "vision",
  "objectives",
  "tanking",
  "assists",
  "ganks / roaming pressure",
  "utility",
  "gold",
  "crowd control",
];

const mvpItems = [
  "parse players from both OP.GG links",
  "verify if Riot IDs exist",
  "reorder players by role",
  "display both teams clearly",
  "see recent champions played",
  "see champion winrate",
  "see champion mastery",
  "see rank and player information",
  "open player details",
  "see recent game history",
  "display each team's global impact vector",
  "compare both teams through an impact difference view",
  "select a champion to update the impact visuals using only that champion's games",
];

const roadmap = [
  {
    title: "Current focus",
    items: [
      "Test the MVP with real amateur teams",
      "Collect feedback from coaches, captains and analysts",
      "Understand if the tool helps with real team preparation",
      "Improve the clarity of the impact visuals",
    ],
  },
  {
    title: "Next",
    items: [
      "Add clearer legends and explanations",
      "Make Recent Impact easier to understand",
      "Improve the scouting page readability",
      "Improve champion-specific impact display",
      "Improve the match preparation workflow",
    ],
  },
  {
    title: "Later",
    items: [
      "Better draft simulation",
      "Training-oriented summaries",
      "Player progression tracking",
      "Team reports",
      "Shareable analysis pages",
      "Public beta",
    ],
  },
];

function Header() {
  return (
    <header style={{ borderBottom: `1px solid ${C.border}`, background: "rgba(8,9,13,0.9)", backdropFilter: "blur(12px)" }}>
      <div style={{ width: "min(1060px, calc(100% - 32px))", margin: "0 auto", height: 66, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, color: C.text, textDecoration: "none" }}>
          <span style={{ width: 36, height: 36, display: "grid", placeItems: "center", borderRadius: 10, background: C.blue, color: "white", fontWeight: 950 }}>IG</span>
          <span style={{ fontWeight: 850 }}>MyImpactGame</span>
        </Link>
        <nav style={{ display: "flex", alignItems: "center", gap: 18, fontSize: 14 }}>
          <Link to="/" style={{ color: C.muted, textDecoration: "none" }}>Home</Link>
          <Link to="/scout" style={{ color: C.text, textDecoration: "none", border: `1px solid ${C.border}`, borderRadius: 10, padding: "9px 12px", background: C.surface, fontWeight: 800 }}>Start Scouting</Link>
        </nav>
      </div>
    </header>
  );
}

function Label({ children }) {
  return (
    <p style={{ margin: "0 0 10px", color: C.blue2, fontSize: 12, fontWeight: 950, textTransform: "uppercase", letterSpacing: "0.08em" }}>
      {children}
    </p>
  );
}

function Section({ label, title, children }) {
  return (
    <section style={{ width: "min(1060px, calc(100% - 32px))", margin: "0 auto", padding: "54px 0" }}>
      <Label>{label}</Label>
      <h2 style={{ margin: 0, color: C.text, fontSize: "clamp(32px, 4vw, 50px)", lineHeight: 1.08, fontWeight: 920 }}>
        {title}
      </h2>
      <div style={{ marginTop: 18, color: C.muted, lineHeight: 1.75, fontSize: 16 }}>
        {children}
      </div>
    </section>
  );
}

function BulletGrid({ items }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 10, marginTop: 18 }}>
      {items.map((item) => (
        <div key={item} style={{ border: `1px solid ${C.border}`, borderRadius: 12, background: C.surface, padding: "12px 14px", color: C.muted, fontSize: 14, lineHeight: 1.5 }}>
          {item}
        </div>
      ))}
    </div>
  );
}

export default function AboutPage() {
  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text }}>
      <Header />
      <main>
        <section style={{ width: "min(1060px, calc(100% - 32px))", margin: "0 auto", padding: "72px 0 42px" }}>
          <Label>About the project</Label>
          <h1 style={{ margin: 0, maxWidth: 880, color: C.text, fontSize: "clamp(44px, 6vw, 76px)", lineHeight: 1.02, fontWeight: 950 }}>
            MyImpactGame
          </h1>
          <p style={{ margin: "22px 0 0", maxWidth: 820, color: C.muted, fontSize: 18, lineHeight: 1.75 }}>
            MyImpactGame is a League of Legends team analysis tool for amateur teams.
          </p>
          <p style={{ margin: "14px 0 0", maxWidth: 820, color: C.muted, fontSize: 18, lineHeight: 1.75 }}>
            It helps coaches, captains, analysts and players regroup the information they need to better understand a team, prepare matches, guide training, and follow player progression.
          </p>
          <p style={{ margin: "14px 0 0", maxWidth: 820, color: C.muted, fontSize: 18, lineHeight: 1.75 }}>
            The goal is not only to help with scouting or draft preparation. The goal is to give a clearer overview of a team and its players.
          </p>
        </section>

        <section style={{ borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, background: "#0b0d13" }}>
          <Section label="Main idea" title="Preparing seriously takes time.">
            <p style={{ margin: 0 }}>
              In amateur League of Legends, creating a roster, joining a new team, preparing a tournament, scouting an opponent, or building a draft plan often means opening many tabs.
            </p>
            <p style={{ margin: "14px 0 0" }}>
              You have to check players one by one, read recent games, champion pools, ranks, winrates and tendencies. Most amateur teams do not have unlimited time or staff.
            </p>
            <p style={{ margin: "14px 0 0" }}>
              MyImpactGame tries to make this easier by regrouping useful information in one place, so teams can read players and teams faster.
            </p>
          </Section>
        </section>

        <Section label="Recent Impact" title="An experimental way to read player tendencies.">
          <p style={{ margin: 0 }}>
            Recent Impact is an experimental stat system based on raw data from recent games. It looks at signals such as:
          </p>
          <BulletGrid items={impactStats} />
          <p style={{ margin: "20px 0 0" }}>
            The idea is to contextualize those stats instead of reading them alone. For each game, the system compares a player's contribution with their own teammates and with their direct opponent.
          </p>
          <p style={{ margin: "14px 0 0" }}>
            Then it calculates an average across multiple games to show recent tendencies. The result is shown as an impact vector, with percentages that roughly represent what the player brought more or less than their direct opponent, while keeping the game context in mind.
          </p>
          <div style={{ marginTop: 20, border: `1px solid ${C.border}`, borderRadius: 16, background: C.panel, padding: 20 }}>
            <strong style={{ display: "block", color: C.text, marginBottom: 8 }}>Example</strong>
            <p style={{ margin: 0 }}>
              If a support has more vision impact than their opponent across several games, it may show that they are consistently contributing more to map control.
            </p>
          </div>
          <p style={{ margin: "18px 0 0", color: C.quiet }}>
            Recent Impact is not a perfect score and should not be used as an absolute truth. It is a tool to help ask better questions about how a player contributes.
          </p>
        </Section>

        <section style={{ borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, background: "#0b0d13" }}>
          <Section label="Current MVP" title="The first version focuses on the Team Scouting Page.">
            <p style={{ margin: 0 }}>
              Users can enter two OP.GG multisearch links and compare two teams. This first version is also the base of the Impact Draft Simulation idea.
            </p>
            <BulletGrid items={mvpItems} />
          </Section>
        </section>

        <section style={{ width: "min(1060px, calc(100% - 32px))", margin: "0 auto", padding: "54px 0 64px" }}>
          <Label>Roadmap</Label>
          <h2 style={{ margin: 0, color: C.text, fontSize: "clamp(32px, 4vw, 50px)", lineHeight: 1.08, fontWeight: 920 }}>
            Build the MVP with real feedback, then expand carefully.
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 14, marginTop: 24 }}>
            {roadmap.map((group) => (
              <article key={group.title} style={{ border: `1px solid ${C.border}`, borderRadius: 18, background: C.surface, padding: 22 }}>
                <h3 style={{ margin: 0, color: group.title === "Current focus" ? C.green : C.text, fontSize: 21 }}>
                  {group.title}
                </h3>
                <ul style={{ margin: "14px 0 0", paddingLeft: 18, color: C.muted, lineHeight: 1.75, fontSize: 14 }}>
                  {group.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <section style={{ borderTop: `1px solid ${C.border}`, background: "#0b0d13" }}>
          <div style={{ width: "min(1060px, calc(100% - 32px))", margin: "0 auto", padding: "42px 0", display: "grid", gridTemplateColumns: "1fr auto", gap: 20, alignItems: "center" }}>
            <div>
              <Label>Try it</Label>
              <h2 style={{ margin: 0, color: C.text, fontSize: 34 }}>Start with two OP.GG multisearch links.</h2>
              <p style={{ margin: "12px 0 0", color: C.muted, lineHeight: 1.7 }}>
                Compare both teams, read the players, then use Recent Impact as an extra layer for scouting and draft preparation.
              </p>
            </div>
            <Link to="/scout" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minHeight: 46, borderRadius: 10, background: C.blue, color: "white", padding: "0 18px", fontWeight: 950, textDecoration: "none", whiteSpace: "nowrap" }}>
              Start Scouting
            </Link>
          </div>
        </section>
      </main>

      <footer style={{ borderTop: `1px solid ${C.border}`, background: "rgba(8,9,13,0.92)" }}>
        <div style={{ width: "min(1060px, calc(100% - 32px))", margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, padding: "18px 0", color: C.muted, fontSize: 12 }}>
          <span>(c) {new Date().getFullYear()} MyImpactGame - Not affiliated with Riot Games.</span>
          <div style={{ display: "flex", gap: 14, fontSize: 18 }}>
            <a href="https://github.com/Rambele/MyLoLStyle" target="_blank" rel="noreferrer" style={{ color: C.muted }}><FaGithub /></a>
            <span onClick={() => window.open("https://discord.com/users/rambel1", "_blank")} style={{ cursor: "pointer", color: C.muted }}><FaDiscord /></span>
          </div>
        </div>
      </footer>

      <style>{`
        @media (max-width: 960px) {
          main section,
          main section > div {
            grid-template-columns: 1fr !important;
          }
        }

        @media (max-width: 720px) {
          header nav {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
